import {useQuery} from "@tanstack/react-query";
import {message} from "antd";
import {AxiosError} from "axios";
import {AnalyticsEvent} from "../../../utils/analytics";
import {ProfileStatus} from "../domain/ProfileStatus";
import {createProfileStatusRepository} from "../data/repositories";

/**
 * React Query cache key for onboarding status.
 *
 * TODO: rename to `["profile_status"]` in a follow-up sweep once
 * the 13 invalidation sites in useBankAccounts/useStripeActions are
 * touched. Kept as `dashboard_status` here to keep this migration scoped.
 */
export const PROFILE_STATUS_QUERY_KEY = ["dashboard_status"] as const;

/**
 * Fetches onboarding status as a ProfileStatus entity.
 * Single source of truth for the Get Started widget and any future consumer
 * (KYC gating, profile-completeness banners, etc.).
 *
 * `enabled` defaults to true for existing (already-authenticated) callers.
 * Pass false explicitly when this hook is mounted before auth is guaranteed
 * (e.g. on the login page) so it doesn't fire an authenticated request too
 * early and get rate-limited.
 */
export const useProfileStatus = (enabled: boolean = true) => {
  const repository = createProfileStatusRepository();

  return useQuery<ProfileStatus, AxiosError<{status: boolean; message: string}>>({
    queryKey: PROFILE_STATUS_QUERY_KEY,
    queryFn: () => repository.getStatus(),
    enabled,
    onError: (err) => {
      message.error(err.message);
      AnalyticsEvent("Dashboard Metrics", "Dashboard Metrics Fail");
    },
    onSuccess: () => {
      AnalyticsEvent("Dashboard Metrics", "Dashboard Metrics Success");
    },
  });
};
